import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Avatar,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { PiStethoscopeBold } from "react-icons/pi";
import {
  useBannedPatientsLazyQuery,
} from "../graphql/types-and-hooks";
import { useBookAppointmentStore } from "../store";
import queryString from "query-string";
import { appointmentTypesStore } from "../store/AppointmentTypesStore";
import { calculateAgeFromDOB } from '../utils/dates';
import { isAgeInRange } from '../utils/isAgeInRange';
import { sessionManager } from '../managers/SessionManager';
import { bookingFlowStore } from '../store/BookingFlowStore';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import type { DoctorWithSlot } from '../types/DoctorWithSlot';
import { getDistanceFromLatLonInKm } from '../utils/getDistanceFromLatLonInKm';
import { getCachedConfig } from '../utils/configManager';
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { useUserData } from '../hooks/useUserData';
import { formatDate, isValidDate, toISOString } from '../lib/util';
import { MdLocationOn } from "react-icons/md";
import { logError } from "../components/logger";
import { doctorsStore } from "../store/DoctorsStore";

// eslint-disable-next-line no-restricted-globals
const parsed = queryString.parse(location.search);

// Access environment variables
const USER_FK = parsed.cid;
const CLINIC_GROUP_CRM = parsed.gid;
const LOCATION_ID = parsed.lid;

interface dateandtime {
  fadate: string;
  fatime: string;
}

interface DoctorCardServiceProps {
  doctor: DoctorWithSlot;
  ddate: dateandtime;
  duration: string;
  userLat?: number | null;
  userLong?: number | null;
  onBookDoctor?: (doctor: DoctorWithSlot) => void; // New prop for booking action
}

const DoctorCardService: React.FC<DoctorCardServiceProps> = observer(({
  doctor,
  ddate,
  duration,
  userLat,
  userLong,
  onBookDoctor
}) => {
  // eslint-disable-next-line
  const [config, setConfig] = useState<any>(null);
  const colors = config?.colorScheme || {};
  // eslint-disable-next-line
  const { display_name, image, gender } = doctor;
  const { fadate, fatime } = ddate;

  // Properly access MobX stores
  // Directly use the store in the render function to establish reactive dependencies
  const selectedAptType = appointmentTypesStore.selectedAptType;

  useEffect(() => {
    const config = getCachedConfig();
    if (config) {
      setConfig(config);
    }
  }, []);

  // eslint-disable-next-line
  const [isDoctorDetailsOpen, setIsDoctorDetailsOpen] = useState(false);

  const distanceInKm =
    doctor.clinic_lat && doctor.clinic_long && userLat && userLong
      ? getDistanceFromLatLonInKm(userLat, userLong, doctor.clinic_lat, doctor.clinic_long).toFixed(2)
      : null;

  const [
    bannedPatientsQuery,
  ] = useBannedPatientsLazyQuery();

  // eslint-disable-next-line
  const [patient, doctor1, serviceType, appointmentType, setData] = useBookAppointmentStore(
    (state) => [
      state.patient,
      state.doctor,
      state.serviceType,
      state.appointmentType,
      state.setData,
      state.resetData,
    ]
  );

  // Create a date object from the slot date and time
  let dateObj;
  let formattedDate = "";

  try {
    if (fadate && fatime) {
      const [day, month, year] = fadate.split("-");
      const [hours, minutes] = fatime.split(":");

      if (day && month && year && hours && minutes) {
        const isoDateString = `${year}-${month}-${day}T${hours}:${minutes}:00`;
        dateObj = new Date(isoDateString);

        if (!isNaN(dateObj.getTime())) {
          formattedDate = formatDate(toISOString(dateObj));
        }
      }
    }
  } catch (error) {
    logError("Error parsing date:", error);
    console.error("Error parsing date:", error);
    formattedDate = "";
  }

  const mapStringToValue = (input) => {
    switch (input) {
      case "F":
        return "Female";
      case "M":
        return "Male";
      default:
        return input;
    }
  };

  const originalString = gender;
  const mappedValue = mapStringToValue(originalString);

  const [isBanned, setIsBanned] = useState(false);
  const [isAgeValid, setIsAgeValid] = useState(true);
  const [ageRangeConfig, setAgeRangeConfig] = useState(null);
  // eslint-disable-next-line
  const { user, isLoggedIn } = useUserData();

  // Get and store the age range config directly from the MobX store
  useEffect(() => {
    if (selectedAptType) {
      setAgeRangeConfig({
        low_age_min: selectedAptType.low_age_min,
        low_age_max: selectedAptType.low_age_max,
        upper_age_min: selectedAptType.upper_age_min,
        upper_age_max: selectedAptType.upper_age_max,
      });
    }
  }, [selectedAptType]); // Properly depend on the MobX observable

  const handleOpenDirections = () => {
    if (!userLat || !userLong || !doctor.clinic_lat || !doctor.clinic_long) {
      console.warn("Missing coordinates");
      return;
    }

    const url = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLong}&destination=${doctor.clinic_lat},${doctor.clinic_long}`;
    window.open(url, '_blank');
  };

  const validatePatient = useCallback(async () => {
    if (!patient || !ageRangeConfig) return;
    try {
      const result = await bannedPatientsQuery({
        variables: {
          input: {
            clinic_group_crm: CLINIC_GROUP_CRM,
            user_fk: USER_FK,
            location_id: LOCATION_ID,
            patient_id: sessionManager.patientId,
          },
        },
      });

      if (sessionManager.patientId) {
        const bannedLevel = parseInt(
          result?.data?.bannedPatients.banned_level || "0"
        );
        if (bannedLevel !== 0) {
          runInAction(() => {
            setIsBanned(true);
          });
          return;
        }
      }

      const patientAge = calculateAgeFromDOB(patient.dob);

      // Now use the stored config instead of accessing MobX store directly
      if (!isAgeInRange(patientAge, ageRangeConfig)) {
        runInAction(() => {
          setIsAgeValid(false);
        });
        return;
      }
    } catch (error) {
      console.error("Validation Error:", error);
    }
  }, [bannedPatientsQuery, patient, ageRangeConfig]);


  // eslint-disable-next-line
  const handleCloseDrawer = () => {
    bookingFlowStore.resetFlow();
    setIsDoctorDetailsOpen(false);
  };

  // Handle booking doctor
  const handleBookDoctor = () => {
    // Set doctor data in local store
    setData({ doctor: doctor });
    doctorsStore.setCurrentDoctor(doctor);
    // console.log("Doctor data set in local store:", doctor);
    // If we have the onBookDoctor prop, use it (for ServiceBookingFlowModal)
    if (onBookDoctor) {
      onBookDoctor(doctor);
    } else {
      // Fallback to the old booking flow if the new one isn't available
      bookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
      bookingFlowStore.setIsModalOpen(true);
      servicebookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
      servicebookingFlowStore.setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (ageRangeConfig) {
      validatePatient();
    }
  }, [validatePatient, ageRangeConfig]);

  // Check if the formatted date is valid using the imported function
  const isFormattedDateValid = isValidDate(formattedDate);

  const isLocationSearchActive = config?.isLocationSearchActive;

  const isExistingPatientOfDoctor = patient?.last_seen_did === doctor?.doctor_id;

  return (
    <Box
      borderWidth="3px"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="lg"
      display="flex"
      flexDirection="row"
      justifyContent={"space-between"}
      mt={8}
      onClick={handleBookDoctor}
    >
      <Box
        bg="#665F5F"
        width={["25px", "35px"]}
        fontSize={["15px", "20px"]}
        display={{ base: "none", md: "flex" }}
        p={[1, 2, 2]}
        pt={14}
      >
        <PiStethoscopeBold color="#fff" />
      </Box>

      <Box width={"100%"}>
        <Box
          display="flex"
          flexDirection={["column", "column", "row"]}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box
            p={{ base: 2, md: 4 }}
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            alignItems={{ base: "center", md: "flex-start" }}
            textAlign={{ base: "center", md: "left" }}
            onClick={handleBookDoctor}
            cursor="pointer"
          >
            <Avatar
              name={display_name}
              mt={{ base: 4, md: 0 }}
              size="xl"
              src={image}
              style={{ boxShadow: "5px 5px 5px #bbb" }}
            >
            </Avatar>
            <Box ml={{ base: 0, md: 4 }} mt={{ base: 4, md: 0 }}>
              <Text
                fontWeight={isLocationSearchActive ? "bold" : "normal"}
                fontSize={{
                  base: isLocationSearchActive ? "30px" : "24px",
                  md: "md"
                }}
              >
                {display_name}
              </Text>

              {isLocationSearchActive && (
                <>
                  <Text fontSize={{ base: "22px", md: "md" }}>
                    {doctor.location_display_name}
                  </Text>
                  <Text fontWeight="bold" fontSize={{ base: "22px", md: "md" }}>
                    {doctor.clinic_address}
                  </Text>
                  <Button
                    onClick={handleOpenDirections}
                    size={{ base: "lg", md: "sm" }}
                    fontSize={{ base: "30px", md: "sm" }}
                    mt={2}
                    _hover={{ bg: colors.buttonBg, color: "white" }}
                    // color={colors.buttonBg}
                    borderRadius="full"
                    leftIcon={<MdLocationOn />}
                  >
                    <Text>{distanceInKm ? `${distanceInKm} km away` : 'Distance unknown'}</Text>
                  </Button>
                </>
              )}


              {!isLocationSearchActive && (
                <>
                  <Text fontSize="xs" color="gray">
                    {mappedValue}
                  </Text>
                  {doctor.relative_time != null && (
                    <Box
                      fontSize={{ base: "30px", md: "xl" }}
                      mt={2}
                      px={4}
                      py={2}
                      borderRadius="full"
                      bg={{ base: "#ffffff", md: "#EAF0F6" }}
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                    >
                      <Text fontSize={{ base: "30px", md: "18px" }} fontWeight="bold" color={colors.buttonBg}>
                        {doctor.relative_time}
                      </Text>
                    </Box>
                  )}
                </>
              )}


            </Box>
          </Box>
          <Box
            p={[2, 4, 4, 4, 4]}
            display="flex"
            flexDirection={["column", "column", "column", "column"]}
            gap={"10px"}
          >
            <Box
              display="flex"
              alignItems="center"
              width="100%"
              minWidth={'275px'}
              justifyContent={"space-between"}
            >
              <Box
                borderRadius="full"
                // height="41px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                {!isLocationSearchActive ? (
                  <>
                    {/* Mobile view: Book this Doctor */}
                    <Box
                      fontSize={{ base: "30px", md: "xl" }}
                      px={4}
                      py={2}
                      borderRadius="full"
                      color={"#fff"}
                      bg={colors.buttonBg}
                      _hover={{ bg: colors.buttonBg, color: "white" }}
                      display={{ base: "block", md: "none" }}
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      onClick={handleBookDoctor}
                      cursor="pointer"
                    >
                      <Text fontSize="24px" fontWeight="bold">
                        Book this Doctor
                      </Text>
                    </Box>

                    {/* Non-mobile view: Time info */}
                    <Box
                      pl="10px"
                      pr="20px"
                      textAlign="center"
                      display={{ base: "none", md: "block" }}
                    >
                      <Text fontSize="lg" as="b" color={colors.buttonBg}>
                        {isFormattedDateValid ? formattedDate : "No time slots available"}
                      </Text>
                      <Text fontSize="sm" mt="-3px" fontWeight="bold" color="#665F5F">
                        First Available Time
                      </Text>
                    </Box>
                  </>
                ) : (
                  // Always show this block if isLocationSearchActive === true
                  <Box pl="10px" pr="20px" textAlign="center">
                    <Text fontSize="lg" as="b" color={colors.buttonBg}>
                      {isFormattedDateValid ? formattedDate : "No time slots available"}
                    </Text>
                    <Text fontSize="sm" mt="-3px" fontWeight="bold" color="#665F5F">
                      First Available Time
                    </Text>
                  </Box>
                )}


              </Box>

            </Box>

            <Box
              display={{ base: "none", md: "flex" }}
              alignItems="center"
              width={"100%"}
              justifyContent={"space-between"}
            >
              <Button
                onClick={handleBookDoctor}
                fontSize="small"
                variant="outline"
                border="2px"
                borderColor={colors.buttonBg}
                color={colors.buttonBg}
                _hover={{ bg: colors.buttonBg, color: "#fff" }}
                width={"100%"}
                borderRadius="30px"
              >
                Book this Doctor
              </Button>
            </Box>
          </Box>
        </Box>

        {doctor?.no_new_patients === "1" && !isExistingPatientOfDoctor && (
          <Alert status="info" >
            <AlertIcon />
            <Box flex="1" >
              <AlertTitle>
                {`${doctor?.display_name} is not available for new patients. `}{" "}
                {!isLoggedIn && (<>
                  Please log in to confirm you are an existing patient and proceed with the booking.
                </>)}
              </AlertTitle>
            </Box>
          </Alert>
        )}

        {patient && (isBanned || !isAgeValid) && (
          <Alert status="error" >
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>
                {isBanned &&
                  "We are not able to complete your booking online. Please call the clinic to make an appointment."}
                {!isAgeValid && `${doctor?.display_name} is not available to book this particular appointment. Please choose an alternative doctor. `}
              </AlertTitle>
            </Box>
          </Alert>
        )}

        <Box
          display={{ base: "flex", md: "none" }}
          alignItems="center"
          justifyContent="center"
          bg={isLocationSearchActive ? colors.buttonBg : undefined}
          textAlign="center"
          height="70px"
          fontWeight="bold"
          fontSize="xl"
          onClick={handleBookDoctor}
          cursor="pointer"
        >
          {!isLocationSearchActive ? (
            <Box
              borderRadius="full"
              height="41px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              flexDirection="column"
              mt={4}
            >
              <Text fontSize="lg" as="b" color={colors.buttonBg}>
                {isFormattedDateValid ? formattedDate : "No time slots available"}
              </Text>
              <Text fontSize="sm" mt="-3px" fontWeight="bold" color={colors.buttonBg} mb={5}>
                First Available Time
              </Text>
            </Box>
          ) : (

            <Text color="white">Book your doctor</Text>
          )}
        </Box>

      </Box>
    </Box>
  );
});

export default DoctorCardService;